import { FilterTh } from '../common/column-filter-indicator'
/** Global directory of HR users across every client. */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys'
import { buttonVariants, Icon } from '@speaking-coach/ui'
import {
  ClearFiltersButton,
  FilterField,
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
} from '../common/list-page'
import { ClientPicker } from '../clients/client-picker'
import {
  ROW_LINK,
  ROW_LINK_ABOVE,
  TABLE,
  TABLE_HEAD_ROW,
  TABLE_ROW,
  TABLE_WRAP,
} from '../common/table'
import { adminHrUsersApi, messageForHrUserError } from './api'
import { InviteStateCell } from '../students/invite-state-cell'

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 250

type LoadStatus = 'loading' | 'loaded' | 'error'

export function HrUsersListPage(): JSX.Element {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const clientId = searchParams.get('clientId') ?? ''
  const [page, setPage] = useState(0)

  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const params = {
    clientId: clientId || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const hrUsersQuery = useQuery({
    queryKey: queryKeys.hrUserList(params),
    queryFn: () => adminHrUsersApi.list(params),
    placeholderData: keepPreviousData,
  })
  const hrUsers = hrUsersQuery.data?.hrUsers ?? []
  const total = hrUsersQuery.data?.total ?? 0
  const status: LoadStatus = hrUsersQuery.isPending
    ? 'loading'
    : hrUsersQuery.isError
      ? 'error'
      : 'loaded'
  const loadError =
    hrUsersQuery.error !== null
      ? messageForHrUserError(hrUsersQuery.error)
      : null
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const hasFilters = search !== '' || clientId !== ''

  function setClientFilter(nextClientId: string): void {
    if (nextClientId) searchParams.set('clientId', nextClientId)
    else searchParams.delete('clientId')
    setSearchParams(searchParams, { replace: true })
    setPage(0)
  }

  function clearFilters(): void {
    setQuery('')
    setSearch('')
    setSearchParams(new URLSearchParams(), { replace: true })
    setPage(0)
  }

  return (
    <ListPage
      title="HR users"
      count={status === 'loaded' ? total : undefined}
      action={
        clientId !== '' ? (
          <Link
            to={`/admin/clients/${encodeURIComponent(clientId)}/hr-users/new`}
            className={
              buttonVariants({ variant: 'primary' }) + ' whitespace-nowrap'
            }
          >
            <Icon name="add" />
            New HR user
          </Link>
        ) : undefined
      }
      search={
        <SearchField
          id="hr-user-search"
          label="Search HR users"
          placeholder="Search family name, given name or email…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(0)
          }}
        />
      }
      activeFilterCount={clientId === '' ? 0 : 1}
      filterAction={
        <ClearFiltersButton hasFilters={hasFilters} onClear={clearFilters} />
      }
      filters={
        <>
          <FilterField active={clientId !== ''} label="Client" htmlFor="hr-user-client-filter">
            <ClientPicker
              id="hr-user-client-filter"
              label="Client"
              emptyLabel="All clients"
              value={clientId}
              onChange={setClientFilter}
              showDetails={false}
              size="sm"
            />
          </FilterField>
        </>
      }
      pagination={
        status === 'loaded' && hrUsers.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </>
            }
            page={page}
            pageCount={pageCount}
            previousDisabled={page === 0}
            nextDisabled={page >= pageCount - 1}
            onPrevious={() => setPage((current) => Math.max(0, current - 1))}
            onNext={() =>
              setPage((current) => Math.min(pageCount - 1, current + 1))
            }
          />
        ) : undefined
      }
    >
      <>
        {status === 'loading' && (
          <p className="text-text-secondary">Loading HR users…</p>
        )}
        {status === 'error' && (
          <div
            role="alert"
            className="bg-surface-card border border-danger/40 p-4 rounded-token-card"
          >
            <p className="font-medium">Could not load HR users</p>
            <p className="text-text-secondary text-body mt-1">{loadError}</p>
          </div>
        )}
        {status === 'loaded' && hrUsers.length === 0 && (
          <FilteredEmptyState
            hasFilters={hasFilters}
            noun="HR users"
            onClear={clearFilters}
            emptyHint="No HR users yet. Open a client to create one."
          />
        )}
        {status === 'loaded' && hrUsers.length > 0 && (
          <div
            className={
              hrUsersQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    <FilterTh className="p-3 font-medium" label="Email" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <FilterTh className="p-3 font-medium" label="Family name" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <FilterTh className="p-3 font-medium" label="Given name" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <FilterTh className="p-3 font-medium" label="Client" filterDescription={clientId ? 'Selected client' : undefined} />
                    <th className="p-3 font-medium">Invitation</th>
                  </tr>
                </thead>
                <tbody>
                  {hrUsers.map((hrUser) => (
                    <tr key={hrUser.userId} className={TABLE_ROW}>
                      <td className="p-3">
                        <Link
                          to={`/admin/clients/${hrUser.clientId}/hr-users/${hrUser.userId}`}
                          className={ROW_LINK}
                        >
                          {hrUser.email}
                        </Link>
                      </td>
                      <td className="p-3">{hrUser.lastName ?? '—'}</td>
                      <td className="p-3">{hrUser.firstName ?? '—'}</td>
                      <td className="p-3 text-text-secondary">
                        <Link
                          to={`/admin/clients/${hrUser.clientId}`}
                          className={`underline ${ROW_LINK_ABOVE}`}
                        >
                          {hrUser.clientName}
                        </Link>
                      </td>
                      <td className="p-3 text-text-secondary">
                        <InviteStateCell student={hrUser} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    </ListPage>
  )
}
